import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Radio, RadioGroup,
  FormControlLabel, FormControl, Checkbox, IconButton,
  InputAdornment, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip, Fade, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Select, MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon, Save as SaveIcon, Clear as ClearIcon,
  CloudDownload, Refresh as RefreshIcon, Add as AddIcon, Close as CloseIcon,
  HealthAndSafety as InsuranceIcon, LocalHospital as DiagnosisIcon,
  CheckCircle as CheckCircleIcon, Delete as DeleteIcon, ContentCopy as CopyIcon,
  //  The pencil beside the diagnosis name — the one field on this screen that
  //  had no way to be edited.
  Edit as EditIcon,
  //  "Can a claim actually see this?" — the check beside the pencil.
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import {
  loadInsurance, loadDiagnoses, getDiagnosisInsurances,
  updateInsuredDiagnosis, deleteInsuredDiagnosis, saveNewDiagnosis,
  bulkApplyDiagnosisInsurance,
  //  Renames the diagnosis across every insurance record filed under it. The
  //  name was the one field on this screen that could not be edited.
  renameDiagnosis,
  //  Runs the claims picker against one catalogue entry and reports whether it
  //  would ever be offered — see checkClaimsSide below.
  catalogueVisibility,
} from '../BaseURL';
import {
  AdminPageHead, ADMIN, EmptyState, BulkEditBar,
  adminFieldSx, brandButtonSx, brandGhostButtonSx,
} from './adminUi';

// ============================================================================
//  Diagnosis Setup
// ============================================================================
//
//  Converted alongside Services Setup, which this screen was built from — so it
//  carried the same defects, in the same places:
//
//    · 143 off-brand colour literals across four palettes: a teal→lime gradient
//      (#11998e → #38ef7d) on the banner, both dialog headers and the table
//      head; an indigo/violet set (#667eea / #764ba2 / #3949ab); a peach panel
//      tint; and a run of pastels (#c5cae9, #90caf9, #ef9a9a) on the empty
//      states.
//    · `height: 580` on both panels, which is what left the band of canvas
//      underneath them.
//    · `minHeight: 520` on the New Diagnosis grid and `minHeight: 280` on its
//      scheme box — the empty space inside that dialog.
//    · `<Grid item xs md size>` throughout, which MUI 7 ignores outright.
//
//  One genuine bug found on the way: the "Apply to Insurances" button was green
//  with an INDIGO hover (#5568d3), left over from the palette it was copied
//  from — so it changed hue on mouseover.

// Section heading inside the dialogs: a small green rule, the name, and a
// hairline to the edge. Groups the fields without stacking heavy boxes.
const DxSectionLabel = ({ children, error }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
    <Box sx={{ width: 3, height: 14, borderRadius: 1, background: error ? '#D64545' : '#2E9E4F', flexShrink: 0 }} />
    <Typography sx={{
      fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
      color: error ? '#D64545' : '#5A6B87', whiteSpace: 'nowrap',
    }}>
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', background: '#DDE5EF' }} />
  </Box>
);

// The bulk-edit bar's fields. Blank means "leave this column alone", so the
// starting state is all-blank and pressing Apply with nothing typed is a no-op.
const EMPTY_BULK = { tariff: '', topup: '', icd: '', core: '' };

const BULK_FIELDS = [
  { key: 'tariff', label: 'Tariff',    type: 'number', adornment: 'GH¢' },
  { key: 'topup',  label: 'Top-up',    type: 'number', adornment: 'GH¢' },
  { key: 'icd',    label: 'ICD-10',    placeholder: 'e.g. I50.1' },
  { key: 'core',   label: 'GDRG Code', placeholder: 'e.g. MEDI07A' },
];

// Selected choice is FILLED, not outlined — an outline alone is what MUI also
// uses for focus, so the tile you tabbed to looked like the one you picked.
const dxChoiceSx = (on) => ({
  m: 0,
  px: 1.25, py: .625,
  borderRadius: '8px',
  border: `1px solid ${on ? '#2E9E4F' : '#DDE5EF'}`,
  background: on ? '#2E9E4F' : '#fff',
  color: on ? '#fff' : '#5A6B87',
  transition: 'border-color .15s ease, background-color .15s ease, color .15s ease',
  '&:hover': { borderColor: on ? '#217A3C' : '#1D4E8F', background: on ? '#217A3C' : '#F4F7FB' },
});

/* ─────────────────────────────────────────────────────────────
   Shared compact styles
───────────────────────────────────────────────────────────── */
const cellSelectSx = {
  fontSize: '0.78rem',
  '& .MuiSelect-select': { py: '4px', px: '8px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DDE5EF' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#123A6D' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#123A6D' },
};
const cellInputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.78rem',
    '& input': { py: '4px', px: '8px' },
    '& fieldset': { borderColor: '#DDE5EF' },
    '&:hover fieldset': { borderColor: '#123A6D' },
    '&.Mui-focused fieldset': { borderColor: '#123A6D' },
  },
};

/* ─────────────────────────────────────────────────────────────
   Editable table row with checkbox selection
───────────────────────────────────────────────────────────── */
const EditableRow = ({ row, idx, selected, onSelectToggle, onChange, onUpdate, onDelete, updating, deleting }) => {
  const tdSx = { borderBottom: '1px solid #DDE5EF', py: 0.75, px: 1 };
  return (
    <TableRow sx={{
      '&:nth-of-type(odd)': { background: selected ? 'rgba(102,126,234,0.12)' : '#f9faff' },
      '&:nth-of-type(even)': { background: selected ? 'rgba(102,126,234,0.12)' : '#fff' },
      '&:hover': { background: selected ? 'rgba(102,126,234,0.18)' : 'rgba(102,126,234,0.04)' },
      transition: 'background 0.2s',
    }}>
      <TableCell sx={{ ...tdSx, width: 48, textAlign: 'center', px: 0.5 }}>
        <Checkbox 
          size="small"
          checked={selected}
          onChange={() => onSelectToggle(row.id)}
          sx={{ 
            color: '#123A6D', 
            '&.Mui-checked': { color: '#123A6D' },
            p: 0.5
          }}
        />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 36, textAlign: 'center', color: '#93A2B8', fontSize: '0.72rem', fontWeight: 500 }}>{idx + 1}</TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 130 }}>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0F1F3D' }}>{row.status || '—'}</Typography>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <TextField size="small" type="number" fullWidth value={row.tariff ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('tariff', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 95 }}>
        <TextField size="small" type="number" fullWidth value={row.topup ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('topup', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <Select size="small" fullWidth value={row.age_cat || ''} onChange={e => onChange('age_cat', e.target.value)} displayEmpty sx={cellSelectSx}>
          <MenuItem value="" disabled><em style={{ color: '#bbb', fontSize: '0.75rem' }}>Select…</em></MenuItem>
          <MenuItem value="Adult">Adult</MenuItem>
          <MenuItem value="Child">Child</MenuItem>
          <MenuItem value="Both">Both</MenuItem>
        </Select>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 110 }}>
        <TextField size="small" fullWidth value={row.icd ?? ''} onChange={e => onChange('icd', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" fullWidth value={row.core ?? ''} onChange={e => onChange('core', e.target.value)} sx={cellInputSx} />
      </TableCell>
      {/*  SPECIALTY, with its code underneath rather than in a column of its own.
           Same change as Procedure Setup, for the same reason: the code had a
           90px column that pushed Actions past the right edge, and it is the one
           field nobody types — it is the first four letters of the G-DRG
           (MEDI07A → MEDI) and arrives filled in from the catalogue. Shown, so
           the row still says what it is; not given a column, so the row fits. It
           is still STORED, still sent on every update, and still editable in the
           New Diagnosis dialog, which is where it is actually set. */}
      <TableCell sx={{ ...tdSx, minWidth: 130 }}>
        <TextField size="small" fullWidth value={row.spe ?? ''} onChange={e => onChange('spe', e.target.value)} sx={cellInputSx} />
        {row.spe_code && (
          <Typography sx={{ fontSize: 10, color: '#93A2B8', mt: .25, pl: .25, letterSpacing: '.03em' }}>
            {row.spe_code}
          </Typography>
        )}
      </TableCell>
      {/*  Icon buttons. Two labelled buttons needed 180px and were the other half
           of why Actions sat past the right edge. The tooltip and the aria-label
           carry the word. */}
      <TableCell sx={{ ...tdSx, width: 96, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip title="Save this row" arrow>
            <span>
              <IconButton size="small" onClick={() => onUpdate(row)} disabled={updating || deleting}
                aria-label={`Save ${row.status || 'this scheme'}`}
                sx={{ color: '#fff', background: '#217A3C', borderRadius: '7px', p: .6,
                  '&:hover': { background: '#1B6631' }, '&:disabled': { background: '#DDE5EF', color: '#93A2B8' } }}>
                {updating ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete this row" arrow>
            <span>
              <IconButton size="small" onClick={() => onDelete(row)} disabled={deleting || updating}
                aria-label={`Delete ${row.status || 'this scheme'}`}
                sx={{ color: '#fff', background: '#D64545', borderRadius: '7px', p: .6,
                  '&:hover': { background: '#B23A3A' }, '&:disabled': { background: '#DDE5EF', color: '#93A2B8' } }}>
                {deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
};

/* ─────────────────────────────────────────────────────────────
   Bulk Apply Dialog - Apply selected row to multiple insurances
───────────────────────────────────────────────────────────── */
const BulkApplyDialog = ({ open, onClose, sourceRow, insuranceProviders, loadingInsurance, onSaved, existingStatuses }) => {
  const [saving, setSaving] = useState(false);
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

  useEffect(() => {
    if (open) {
      // Reset selections when dialog opens
      setSelectedInsurances({});
      setInsuranceSearch('');
    }
  }, [open]);

  const handleInsuranceToggle = (id) => {
    setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { 
      // Only select if not already assigned
      if (!existingStatuses.includes(i.name)) {
        all[i.id] = true; 
      }
    });
    setSelectedInsurances(all);
  };

  const handleDeselectAll = () => setSelectedInsurances({});

  const handleApply = async () => {
    if (selectedCount === 0) {
      await Swal.fire({
        title: 'No Selection',
        text: 'Please select at least one insurance provider to apply this diagnosis to.',
        icon: 'warning',
        confirmButtonColor: '#2E9E4F',
      });
      return;
    }

    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        return {
          age_cat: sourceRow.age_cat,
          name: sourceRow.name,
          cat: sourceRow.cat,
          icd: sourceRow.icd,
          core: sourceRow.core,
          tariff: parseFloat(sourceRow.tariff),
          topup: parseFloat(sourceRow.topup),
          spe: sourceRow.spe,
          spe_code: sourceRow.spe_code,
          status: provider?.name || ''
        };
      });

      const response = await axios.post(bulkApplyDiagnosisInsurance, { rows });

      if (response.data.success) {
        await Swal.fire({
          title: 'Applied!',
          html: `<b>${sourceRow.name}</b> applied to <b>${selectedCount}</b> insurance provider${selectedCount !== 1 ? 's' : ''}.`,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        if (onSaved) onSaved();
        handleClose();
      } else {
        throw new Error(response.data.message || 'Apply failed');
      }
    } catch (err) {
      await Swal.fire({
        title: 'Apply Failed',
        text: err.response?.data?.message || err.message || 'An error occurred while applying',
        icon: 'error',
        confirmButtonColor: '#D64545',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedInsurances({});
    setInsuranceSearch('');
    onClose();
  };

  const filteredProviders = insuranceProviders.filter(i =>
    i.name?.toLowerCase().includes(insuranceSearch.toLowerCase())
  );

  const availableProviders = filteredProviders.filter(i => !existingStatuses.includes(i.name));
  const alreadyAssignedProviders = filteredProviders.filter(i => existingStatuses.includes(i.name));

  if (!sourceRow) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' } }}>

      <DialogTitle sx={{ background: 'linear-gradient(135deg,#123A6D,#0B2452)', color: 'white', px: { xs: 2, sm: 2.5 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
            background: 'linear-gradient(135deg,#3FBF64,#217A3C)',
            display: 'grid', placeItems: 'center',
          }}>
            <CopyIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>Copy to other schemes</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,.75)', mt: .2 }}>
              Applies this row's codes and pricing to the schemes you pick
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small"
          sx={{ color: 'white', flexShrink: 0, bgcolor: 'rgba(255,255,255,.12)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#F4F7FB' }}>
        {/* Source row summary. Was a 7-cell `<Grid>` of bold-label pairs, which
            in MUI 7 collapsed into a single column of run-together lines. A
            definition grid now: label above value, so it scans as a record. */}
        <Paper elevation={0} sx={{ p: 2, mb: 2.5, background: '#fff', border: '1px solid #DDE5EF', borderRadius: ADMIN.radiusSm }}>
          <DxSectionLabel>Copying from</DxSectionLabel>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0F1F3D', mb: 1.5 }}>
            {sourceRow.name}
          </Typography>
          <Box sx={{
            display: 'grid', gap: 1.5,
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
          }}>
            {[
              ['ICD',       sourceRow.icd],
              ['Core/GDRG', sourceRow.core],
              ['Specialty', sourceRow.spe_code],
              ['Tariff',    sourceRow.tariff != null ? `GH¢ ${sourceRow.tariff}` : null],
              ['Top-up',    sourceRow.topup  != null ? `GH¢ ${sourceRow.topup}`  : null],
              ['Applies to', sourceRow.age_cat],
            ].map(([label, value]) => (
              <Box key={label} sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.8px', textTransform: 'uppercase', color: '#93A2B8' }}>
                  {label}
                </Typography>
                <Typography noWrap title={value || '—'} sx={{ fontSize: 12.5, fontWeight: 600, color: value ? '#0F1F3D' : '#93A2B8' }}>
                  {value || '—'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Insurance Selection */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#123A6D', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
              Select Insurance Providers
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedCount} of {availableProviders.length} available selected
            </Typography>
          </Box>
          {selectedCount > 0 && (
            <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
              label={`${selectedCount} selected`}
              sx={{ background: '#E7F5EC', color: '#217A3C', fontWeight: 600, fontSize: '0.7rem' }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}
            sx={{ borderColor: '#123A6D', color: '#123A6D', fontSize: '0.72rem', py: 0.4 }}>
            Select All Available
          </Button>
          <Button size="small" variant="outlined" onClick={handleDeselectAll}
            sx={{ borderColor: '#123A6D', color: '#123A6D', fontSize: '0.72rem', py: 0.4 }}>
            Deselect All
          </Button>
        </Box>

        <TextField fullWidth size="small" placeholder="Search insurance..."
          value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)} sx={{ mb: 1.5 }}
          InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#93A2B8' }} /></InputAdornment>) }} />

        <Box sx={{
          border: '1px solid #E8EEF7', borderRadius: 2, maxHeight: 320, overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' },
        }}>
          {loadingInsurance ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {/* Available Providers */}
              {availableProviders.length > 0 && (
                <Box sx={{ p: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#217A3C', display: 'block', mb: 0.5, px: 1 }}>
                    Available ({availableProviders.length})
                  </Typography>
                  {availableProviders.map(insurance => (
                    <Box key={insurance.id} onClick={() => handleInsuranceToggle(insurance.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1,
                        cursor: 'pointer', mb: 0.5,
                        background: selectedInsurances[insurance.id] ? 'rgba(18,58,109,0.06)' : 'transparent',
                        border: selectedInsurances[insurance.id] ? '1px solid rgba(18,58,109,0.22)' : '1px solid transparent',
                        transition: 'all 0.15s ease',
                        '&:hover': { background: 'rgba(18,58,109,0.05)' },
                      }}>
                      <Checkbox size="small"
                        checked={selectedInsurances[insurance.id] || false}
                        onChange={() => handleInsuranceToggle(insurance.id)}
                        onClick={e => e.stopPropagation()}
                        sx={{ p: 0.5, color: '#123A6D', '&.Mui-checked': { color: '#123A6D' } }} />
                      <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem', color: '#0F1F3D' }}>
                        {insurance.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Already Assigned Providers */}
              {alreadyAssignedProviders.length > 0 && (
                <Box sx={{ p: 1, borderTop: availableProviders.length > 0 ? '1px solid #DDE5EF' : 'none' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#D64545', display: 'block', mb: 0.5, px: 1 }}>
                    Already Assigned ({alreadyAssignedProviders.length})
                  </Typography>
                  {alreadyAssignedProviders.map(insurance => (
                    <Box key={insurance.id}
                      sx={{
                        display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1,
                        mb: 0.5, background: 'rgba(229,57,53,0.05)', opacity: 0.6,
                        border: '1px solid rgba(229,57,53,0.1)',
                      }}>
                      <Checkbox size="small" disabled checked={false}
                        sx={{ p: 0.5 }} />
                      <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem', color: '#666' }}>
                        {insurance.name}
                      </Typography>
                      <Chip label="Exists" size="small" sx={{ ml: 'auto', fontSize: '0.65rem', height: 18, background: '#FDECEC', color: '#B23A3A' }} />
                    </Box>
                  ))}
                </Box>
              )}

              {availableProviders.length === 0 && alreadyAssignedProviders.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                  <Typography variant="body2" color="text.secondary">No providers found</Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #DDE5EF', background: '#F4F7FB', gap: 1 }}>
        <Button onClick={handleClose} startIcon={<ClearIcon />} variant="outlined" disabled={saving}
          sx={{ color: '#5A6B87', borderColor: '#DDE5EF', '&:hover': { background: '#f5f5f5' } }}>
          Cancel
        </Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CopyIcon />}
          onClick={handleApply} disabled={saving || selectedCount === 0}
          sx={{
            background: 'linear-gradient(135deg,#3FBF64,#217A3C)', color: 'white', fontWeight: 700, px: 3,
            boxShadow: '0 4px 14px rgba(102,126,234,0.35)',
            '&:hover': { background: 'linear-gradient(135deg,#2E9E4F,#217A3C)', boxShadow: '0 6px 18px rgba(102,126,234,0.45)' },
            '&:disabled': { background: '#DDE5EF', color: '#93A2B8' },
          }}>
          {saving ? 'Applying...' : `Apply to ${selectedCount} Provider${selectedCount !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────────────────────
   New-Diagnosis Dialog
───────────────────────────────────────────────────────────── */
const NewDiagnosisDialog = ({ open, onClose, insuranceProviders, loadingInsurance, onSaved }) => {
  const [saving, setSaving] = useState(false);

  // ── form state ────────────────────────────────────────────
  const [ageCategory, setAgeCategory] = useState('');
  const [diagnosisName, setDiagnosisName] = useState('');
  const [category, setCategory] = useState('');
  const [icdCode, setIcdCode] = useState('');
  const [corePackage, setCorePackage] = useState('');
  const [tariff, setTariff] = useState('');
  const [topUp, setTopUp] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [specialtyCode, setSpecialtyCode] = useState('');
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [errors, setErrors] = useState({});

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

  const clearError = (field) => setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

  const handleInsuranceToggle = (id) => {
    setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
    clearError('insurance');
  };
  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { all[i.id] = true; });
    setSelectedInsurances(all);
    clearError('insurance');
  };
  const handleDeselectAll = () => setSelectedInsurances({});

  /* ── validation ──────────────────────────────────────────── */
  const validate = () => {
    const e = {};

    if (!ageCategory)
      e.ageCategory = 'Age Category is required';

    if (!diagnosisName.trim())
      e.diagnosisName = 'Diagnosis Name is required';

    if (!category.trim())
      e.category = 'Category is required';

    if (!icdCode.trim())
      e.icdCode = 'ICD Code is required';

    if (!corePackage.trim())
      e.corePackage = 'Core Package is required';

    // Tariff: required, must be a number ≥ 0
    if (tariff === '' || tariff === null || tariff === undefined)
      e.tariff = 'Tariff is required';
    else if (isNaN(parseFloat(tariff)) || parseFloat(tariff) < 0)
      e.tariff = 'Must be a valid number (0 or more)';

    // Top-up: required, must be a number ≥ 0
    if (topUp === '' || topUp === null || topUp === undefined)
      e.topUp = 'Top-up is required';
    else if (isNaN(parseFloat(topUp)) || parseFloat(topUp) < 0)
      e.topUp = 'Must be a valid number (0 or more)';

    if (!specialty.trim())
      e.specialty = 'Specialty is required';

    if (!specialtyCode.trim())
      e.specialtyCode = 'Specialty Code is required';

    if (selectedCount === 0)
      e.insurance = 'At least one insurance provider must be selected';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── save handler ────────────────────────────────────────── */
  const handleSave = async () => {
    if (!validate()) {
      const pending = {
        ...(!ageCategory ? { a: 'Age Category must be selected' } : {}),
        ...(!diagnosisName.trim() ? { b: 'Diagnosis Name must be entered' } : {}),
        ...(!category.trim() ? { c: 'Category must be entered' } : {}),
        ...(!icdCode.trim() ? { d: 'ICD Code must be entered' } : {}),
        ...(!corePackage.trim() ? { e: 'Core Package must be entered' } : {}),
        ...(tariff === '' ? { f: 'Tariff must be entered' } : {}),
        ...(topUp === '' ? { g: 'Top-up must be entered' } : {}),
        ...(!specialty.trim() ? { h: 'Specialty must be entered' } : {}),
        ...(!specialtyCode.trim() ? { i: 'Specialty Code must be entered' } : {}),
        ...(selectedCount === 0 ? { j: 'At least one insurance must be selected' } : {}),
      };
      await Swal.fire({
        title: 'Please complete all required fields',
        html: `<ul style="text-align:left;margin:0;padding-left:1.2em;">${Object.values(pending).map(m => `<li>${m}</li>`).join('')}</ul>`,
        icon: 'warning',
        confirmButtonColor: '#2E9E4F',
      });
      return;
    }

    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        return {
          age_cat: ageCategory,
          name: diagnosisName.trim(),
          cat: category.trim(),
          icd: icdCode.trim(),
          core: corePackage.trim(),
          tariff: parseFloat(tariff),
          topup: parseFloat(topUp),
          spe: specialty.trim(),
          spe_code: specialtyCode.trim(),
          status: provider?.name || ''
        };
      });

      const response = await axios.post(saveNewDiagnosis, { rows });

      if (response.data.success) {
        await Swal.fire({
          title: 'Saved!',
          html: `<b>${diagnosisName}</b> saved with <b>${selectedCount}</b> insurance record${selectedCount !== 1 ? 's' : ''}.`,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        if (onSaved) onSaved();
        handleClose();
      } else {
        throw new Error(response.data.message || 'Save failed');
      }
    } catch (err) {
      await Swal.fire({
        title: 'Save Failed',
        text: err.response?.data?.message || err.message || 'An error occurred while saving',
        icon: 'error',
        confirmButtonColor: '#D64545',
      });
    } finally {
      setSaving(false);
    }
  };

  /* ── reset & close ───────────────────────────────────────── */
  const handleClose = () => {
    setAgeCategory(''); setDiagnosisName(''); setCategory('');
    setIcdCode(''); setCorePackage(''); setTariff(''); setTopUp('');
    setSpecialty(''); setSpecialtyCode('');
    setSelectedInsurances({}); setInsuranceSearch('');
    setErrors({});
    onClose();
  };

  const filteredProviders = insuranceProviders.filter(i =>
    i.name?.toLowerCase().includes(insuranceSearch.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' } }}>

      <DialogTitle sx={{ background: 'linear-gradient(135deg,#123A6D,#0B2452)', color: 'white', px: { xs: 2, sm: 2.5 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
            background: 'linear-gradient(135deg,#3FBF64,#217A3C)',
            display: 'grid', placeItems: 'center',
          }}>
            <DiagnosisIcon sx={{ fontSize: 19 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>New Diagnosis</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,.75)', mt: .2 }}>
              Its codes, what it costs, and which schemes it applies to
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small"
          sx={{ color: 'white', flexShrink: 0, bgcolor: 'rgba(255,255,255,.12)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Stacked sections, full width. Two columns put nine fields on the left
            and a search box plus two checkboxes on the right, so half the
            dialog was empty and the divider pointed straight at it. */}
        <Box sx={{ p: { xs: 2, sm: 2.5 }, display: 'grid', gap: 2.5 }}>

          {/* ── Identity ──────────────────────────────────── */}
          <Box>
            <DxSectionLabel>The diagnosis</DxSectionLabel>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' } }}>
              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <TextField fullWidth size="small" label="Diagnosis Name *" value={diagnosisName}
                  onChange={e => { setDiagnosisName(e.target.value); clearError('diagnosisName'); }}
                  error={!!errors.diagnosisName} helperText={errors.diagnosisName || ' '} sx={adminFieldSx} />
              </Box>

              <TextField fullWidth size="small" label="Category *" value={category}
                onChange={e => { setCategory(e.target.value); clearError('category'); }}
                error={!!errors.category} helperText={errors.category || ' '} sx={adminFieldSx}
                placeholder="e.g. CARDIAC DISEASES" />

              <TextField fullWidth size="small" label="Specialty *" value={specialty}
                onChange={e => { setSpecialty(e.target.value); clearError('specialty'); }}
                error={!!errors.specialty} helperText={errors.specialty || ' '} sx={adminFieldSx}
                placeholder="e.g. PAEDIATRICS" />

              <TextField fullWidth size="small" label="Specialty Code *" value={specialtyCode}
                onChange={e => { setSpecialtyCode(e.target.value); clearError('specialtyCode'); }}
                error={!!errors.specialtyCode} helperText={errors.specialtyCode || ' '} sx={adminFieldSx}
                placeholder="e.g. PAED" />
            </Box>
          </Box>

          {/* ── Codes ─────────────────────────────────────── */}
          <Box>
            <DxSectionLabel error={!!(errors.icdCode || errors.corePackage)}>Codes</DxSectionLabel>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' } }}>
              <TextField fullWidth size="small" label="ICD Code *" value={icdCode}
                onChange={e => { setIcdCode(e.target.value); clearError('icdCode'); }}
                error={!!errors.icdCode} helperText={errors.icdCode || 'The ICD-10 code'} sx={adminFieldSx}
                placeholder="e.g. I50.1" />

              <TextField fullWidth size="small" label="Core Package / GDRG *" value={corePackage}
                onChange={e => { setCorePackage(e.target.value); clearError('corePackage'); }}
                error={!!errors.corePackage} helperText={errors.corePackage || 'The GDRG the claim is billed on'} sx={adminFieldSx}
                placeholder="e.g. MEDI07A" />
            </Box>
          </Box>

          {/* ── Pricing + who it applies to ───────────────── */}
          <Box>
            <DxSectionLabel error={!!(errors.tariff || errors.topUp || errors.ageCategory)}>Pricing</DxSectionLabel>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' } }}>
              <TextField fullWidth size="small" label="Tariff *" type="number"
                value={tariff}
                onChange={e => { setTariff(e.target.value); clearError('tariff'); }}
                error={!!errors.tariff} helperText={errors.tariff || 'What the scheme pays'}
                InputProps={{ startAdornment: <InputAdornment position="start"><Box component="span" sx={{ fontSize: 12, color: '#93A2B8', fontWeight: 600 }}>GH¢</Box></InputAdornment> }}
                inputProps={{ min: 0, step: '0.01' }} sx={adminFieldSx} />

              <TextField fullWidth size="small" label="Top-up *" type="number"
                value={topUp}
                onChange={e => { setTopUp(e.target.value); clearError('topUp'); }}
                error={!!errors.topUp} helperText={errors.topUp || 'What the patient pays'}
                InputProps={{ startAdornment: <InputAdornment position="start"><Box component="span" sx={{ fontSize: 12, color: '#93A2B8', fontWeight: 600 }}>GH¢</Box></InputAdornment> }}
                inputProps={{ min: 0, step: '0.01' }} sx={adminFieldSx} />

              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 11, letterSpacing: '.5px', textTransform: 'uppercase', color: errors.ageCategory ? '#D64545' : '#93A2B8', mb: .75, display: 'block' }}>
                  Applies to *
                </Typography>
                <RadioGroup row value={ageCategory} sx={{ gap: .75 }}
                  onChange={e => { setAgeCategory(e.target.value); clearError('ageCategory'); }}>
                  {['Adult', 'Child', 'Both'].map(v => (
                    <FormControlLabel key={v} value={v} sx={dxChoiceSx(ageCategory === v)}
                      control={<Radio size="small" sx={{ p: .25, mr: .5, color: '#93A2B8', '&.Mui-checked': { color: '#fff' } }} />}
                      label={<Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{v}</Typography>} />
                  ))}
                </RadioGroup>
                {errors.ageCategory && <Typography sx={{ color: '#D64545', fontSize: 11, mt: .5 }}>{errors.ageCategory}</Typography>}
              </FormControl>
            </Box>
          </Box>

          {/* ── RIGHT: insurance checkboxes ────────────────── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <DxSectionLabel error={!!errors.insurance}>
              Insurance schemes *
              <Box component="span" sx={{
                ml: 1, letterSpacing: 0, textTransform: 'none',
                color: selectedCount > 0 ? '#217A3C' : '#93A2B8',
              }}>
                {selectedCount} of {insuranceProviders.length} selected
              </Box>
            </DxSectionLabel>
            <Box sx={{ display: 'none' }}>
              {selectedCount > 0 && (
                <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                  label={`${selectedCount} selected`}
                  sx={{ background: '#E7F5EC', color: '#217A3C', fontWeight: 600, fontSize: '0.7rem' }} />
              )}
            </Box>

            {errors.insurance && (
              <Typography variant="caption" sx={{ color: '#D64545', fontSize: '0.7rem', mb: 0.5 }}>
                {errors.insurance}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 1.25, alignItems: 'center' }}>
              <TextField fullWidth size="small" placeholder="Search schemes…"
                value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)}
                sx={adminFieldSx}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: '#93A2B8' }} /></InputAdornment>) }} />
              {/* Text links, not two outlined buttons the same size as Save. */}
              <Button size="small" onClick={handleSelectAll}
                sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, color: '#123A6D', whiteSpace: 'nowrap', minWidth: 0, px: 1 }}>
                All
              </Button>
              <Button size="small" onClick={handleDeselectAll}
                sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, color: '#5A6B87', whiteSpace: 'nowrap', minWidth: 0, px: 1 }}>
                None
              </Button>
            </Box>

            {/* Grid of tiles, no reserved minHeight. The old box held 280px of
                white to show two checkboxes stacked down its left edge. */}
            <Box sx={{
              overflowY: 'auto',
              border: `1px solid ${errors.insurance ? '#D64545' : '#DDE5EF'}`,
              borderRadius: '10px', p: .875, maxHeight: 260,
              transition: 'border-color 0.2s',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' },
            }}>
              {loadingInsurance ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : filteredProviders.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                  <Typography sx={{ fontSize: 12.5, color: '#5A6B87' }}>No schemes found</Typography>
                </Box>
              ) : (
                <Box sx={{
                  display: 'grid', gap: .75,
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
                }}>
                {filteredProviders.map(insurance => {
                  const on = !!selectedInsurances[insurance.id];
                  return (
                  <Box key={insurance.id} onClick={() => handleInsuranceToggle(insurance.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', px: 1, py: .625, borderRadius: '8px',
                      cursor: 'pointer', minWidth: 0,
                      background: on ? '#E7F5EC' : '#fff',
                      border: `1px solid ${on ? '#2E9E4F' : '#DDE5EF'}`,
                      transition: 'background-color .15s ease, border-color .15s ease',
                      '&:hover': { borderColor: on ? '#217A3C' : '#1D4E8F' },
                    }}>
                    <Checkbox size="small"
                      checked={on}
                      onChange={() => handleInsuranceToggle(insurance.id)}
                      onClick={e => e.stopPropagation()}
                      sx={{ p: 0.25, flexShrink: 0, color: '#93A2B8', '&.Mui-checked': { color: '#2E9E4F' } }} />
                    <Typography noWrap title={insurance.name}
                      sx={{ ml: .75, fontSize: 12, fontWeight: on ? 700 : 500, color: on ? '#217A3C' : '#0F1F3D' }}>
                      {insurance.name}
                    </Typography>
                  </Box>
                  );
                })}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: { xs: 2, sm: 2.5 }, py: 1.75,
        borderTop: '1px solid #DDE5EF', background: '#fff', gap: 1.25,
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        '& > *': { width: { xs: '100%', sm: 'auto' }, ml: { xs: '0 !important', sm: undefined } },
      }}>
        <Button onClick={handleClose} startIcon={<ClearIcon />} variant="outlined" disabled={saving}
          sx={{ ...brandGhostButtonSx, py: 1, px: 2.5, fontSize: 13.5 }}>
          Cancel
        </Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
          onClick={handleSave} disabled={saving}
          sx={{ ...brandButtonSx, py: 1, px: 2.5, fontSize: 13.5 }}>
          {saving ? 'Saving…' : 'Save diagnosis'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────── */
const Diagnosis = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [loadingDiagnoses, setLoadingDiagnoses] = useState(false);
  const [loadingDiagnosisInsurances, setLoadingDiagnosisInsurances] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState(null);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);
  const [diagnosisInsuranceRows, setDiagnosisInsuranceRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkApplyDialogOpen, setBulkApplyDialogOpen] = useState(false);
  const [sourceRowForBulkApply, setSourceRowForBulkApply] = useState(null);
  const [bulkValues, setBulkValues] = useState(EMPTY_BULK);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const loadInsuranceProviders = async () => {
    setLoadingInsurance(true); setError(null);
    try {
      const res = await axios.get(loadInsurance);
      if (res.data.success) setInsuranceProviders(res.data.results);
      else setError('Failed to load insurance providers');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load insurance providers');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load insurance providers', icon: 'error' });
    } finally { setLoadingInsurance(false); }
  };

  const loadDiagnosesFromBackend = async () => {
    setLoadingDiagnoses(true); setError(null);
    try {
      const res = await axios.get(loadDiagnoses);
      if (res.data.success) setDiagnoses(res.data.results);
      else setError('Failed to load diagnoses');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load diagnoses');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load diagnoses', icon: 'error' });
    } finally { setLoadingDiagnoses(false); }
  };

  const loadDiagnosisInsurancesForDisplay = async (diagnosisId, diagnosisName) => {
    setLoadingDiagnosisInsurances(true); setDiagnosisInsuranceRows([]); setSelectedRowIds([]);
    try {
      const res = await axios.post(getDiagnosisInsurances, { diagnosisId, diagnosisName });
      if (res.data.success) setDiagnosisInsuranceRows(res.data.results);
    } catch (err) {
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load diagnosis insurances', icon: 'error' });
    } finally { setLoadingDiagnosisInsurances(false); }
  };

  const handleUpdateRow = async (row) => {
    if (!row.tariff || !row.topup || !row.age_cat) {
      await Swal.fire({ title: 'Validation Error', text: 'Please fill in all required fields (Tariff, Top-up, Age Category)', icon: 'warning' }); return;
    }
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot update.', icon: 'error' }); return; }
    setUpdatingRowId(row.id);
    try {
      const res = await axios.post(updateInsuredDiagnosis, {
        id: row.id,
        tariff: parseFloat(row.tariff),
        topup: parseFloat(row.topup),
        age_cat: row.age_cat,
        icd: row.icd || '',
        core: row.core || '',
        spe: row.spe || '',
        spe_code: row.spe_code || ''
      });
      if (res.data.success) {
        await Swal.fire({ title: 'Success!', text: `Successfully updated ${row.status}`, icon: 'success', timer: 2000, showConfirmButton: false });
        if (selectedDiagnosis) await loadDiagnosisInsurancesForDisplay(selectedDiagnosis.id, selectedDiagnosis.name);
      } else throw new Error(res.data.message || 'Update failed');
    } catch (err) {
      await Swal.fire({ title: 'Update Failed', text: err.response?.data?.message || err.message || 'Failed to update', icon: 'error' });
    } finally { setUpdatingRowId(null); }
  };

  const handleDeleteRow = async (row) => {
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot delete.', icon: 'error' }); return; }
    const result = await Swal.fire({
      title: 'Confirm Delete',
      html: `Are you sure you want to delete the record for <strong>${row.status || 'this entry'}</strong>?<br/><span style="color:#D64545;font-size:0.9rem;">This action cannot be undone.</span>`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#D64545', cancelButtonColor: '#93A2B8',
      confirmButtonText: 'Yes, Delete', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setDeletingRowId(row.id);
    try {
      const res = await axios.post(deleteInsuredDiagnosis, { id: row.id });
      if (res.data.success) {
        await Swal.fire({ title: 'Deleted!', text: `Record for "${row.status}" has been deleted.`, icon: 'success', timer: 2000, showConfirmButton: false });
        setDiagnosisInsuranceRows(prev => prev.filter(r => r.id !== row.id));
        setSelectedRowIds(prev => prev.filter(id => id !== row.id));
      } else throw new Error(res.data.message || 'Delete failed');
    } catch (err) {
      await Swal.fire({ title: 'Delete Failed', text: err.response?.data?.message || err.message || 'Failed to delete', icon: 'error' });
    } finally { setDeletingRowId(null); }
  };

  const handleRowSelectionToggle = (rowId) => {
    setSelectedRowIds(prev => 
      prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
    );
  };

  const handleSelectAllRows = () => {
    if (selectedRowIds.length === diagnosisInsuranceRows.length) {
      setSelectedRowIds([]);
    } else {
      setSelectedRowIds(diagnosisInsuranceRows.map(r => r.id));
    }
  };

  const handleBulkApply = () => {
    if (selectedRowIds.length !== 1) {
      Swal.fire({
        title: 'Selection Required',
        text: 'Please select exactly ONE row to use as template for bulk insurance assignment.',
        icon: 'info',
        confirmButtonColor: '#2E9E4F',
      });
      return;
    }

    const sourceRow = diagnosisInsuranceRows.find(r => r.id === selectedRowIds[0]);
    if (!sourceRow) return;

    setSourceRowForBulkApply(sourceRow);
    setBulkApplyDialogOpen(true);
  };

  /* ─────────────────────────────────────────────────────────────
     Bulk edit bar — apply typed values / delete, across ticked rows
     ───────────────────────────────────────────────────────────── */
  //
  //  Both loop the EXISTING single-row endpoints rather than calling a new bulk
  //  one. Deliberate: /updateInsuredDiagnosis and /deleteInsuredDiagnosis are
  //  already written, validated and in daily use, and the number of schemes on
  //  one diagnosis is small (single figures). A new bulk SQL path would be
  //  faster and completely untested — the wrong trade on records that price
  //  claims.
  //
  //  Failures are counted rather than thrown, and the ROW LIST IS RELOADED at
  //  the end. If scheme 3 of 6 fails, the first two really did change; showing
  //  the optimistic values for all six would be a lie about what is stored.

  const bulkTargets = () => diagnosisInsuranceRows.filter(r => selectedRowIds.includes(r.id));

  const handleBarApply = async () => {
    const targets = bulkTargets();
    if (targets.length === 0) return;

    // Blank means "leave alone". Only what the user actually typed is applied.
    const patch = {};
    if (String(bulkValues.tariff).trim() !== '') patch.tariff = bulkValues.tariff.trim();
    if (String(bulkValues.topup).trim()  !== '') patch.topup  = bulkValues.topup.trim();
    if (String(bulkValues.icd).trim()    !== '') patch.icd    = bulkValues.icd.trim();
    if (String(bulkValues.core).trim()   !== '') patch.core   = bulkValues.core.trim();
    if (Object.keys(patch).length === 0) return;

    const confirm = await Swal.fire({
      title: `Apply to ${targets.length} scheme${targets.length === 1 ? '' : 's'}?`,
      html: `<div style="text-align:left;font-size:13px">
               ${Object.entries(patch).map(([k, v]) => `<div><b>${k}</b> → ${v}</div>`).join('')}
               <div style="margin-top:8px;color:#5A6B87">Fields left blank keep their current values.</div>
             </div>`,
      icon: 'question', showCancelButton: true,
      confirmButtonColor: '#123A6D', cancelButtonColor: '#5A6B87',
      confirmButtonText: 'Yes, apply',
    });
    if (!confirm.isConfirmed) return;

    setBulkApplying(true);
    let ok = 0; const failed = [];
    for (const row of targets) {
      try {
        // The endpoint updates every column it receives, so the row's CURRENT
        // values are sent for anything the patch does not cover — otherwise a
        // blank field here would wipe the stored one.
        const res = await axios.post(updateInsuredDiagnosis, {
          id:       row.id,
          tariff:   patch.tariff ?? row.tariff,
          topup:    patch.topup  ?? row.topup,
          age_cat:  row.age_cat,
          icd:      patch.icd    ?? row.icd,
          core:     patch.core   ?? row.core,
          spe:      row.spe,
          spe_code: row.spe_code,
        });
        if (res.data.success) ok++; else failed.push(row.status || row.id);
      } catch (_) { failed.push(row.status || row.id); }
    }
    setBulkApplying(false);

    if (selectedDiagnosis) {
      await loadDiagnosisInsurancesForDisplay(selectedDiagnosis.id, selectedDiagnosis.name);
    }
    setBulkValues(EMPTY_BULK);

    await Swal.fire({
      title: failed.length === 0 ? 'Applied' : 'Partly applied',
      text: failed.length === 0
        ? `${ok} scheme${ok === 1 ? '' : 's'} updated.`
        : `${ok} updated. Failed: ${failed.join(', ')}.`,
      icon: failed.length === 0 ? 'success' : 'warning',
      timer: failed.length === 0 ? 2200 : undefined,
      showConfirmButton: failed.length > 0,
    });
  };

  const handleBarDelete = async () => {
    const targets = bulkTargets();
    if (targets.length === 0) return;

    const confirm = await Swal.fire({
      title: `Delete ${targets.length} record${targets.length === 1 ? '' : 's'}?`,
      html: `<div style="text-align:left;font-size:13px">
               ${targets.map(r => `<div>• ${r.status || r.id}</div>`).join('')}
               <div style="margin-top:8px;color:#B23A3A">This cannot be undone.</div>
             </div>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#D64545', cancelButtonColor: '#5A6B87',
      confirmButtonText: `Yes, delete ${targets.length}`,
    });
    if (!confirm.isConfirmed) return;

    setBulkDeleting(true);
    let ok = 0; const failed = [];
    for (const row of targets) {
      try {
        const res = await axios.post(deleteInsuredDiagnosis, { id: row.id });
        if (res.data.success) ok++; else failed.push(row.status || row.id);
      } catch (_) { failed.push(row.status || row.id); }
    }
    setBulkDeleting(false);
    setSelectedRowIds([]);

    if (selectedDiagnosis) {
      await loadDiagnosisInsurancesForDisplay(selectedDiagnosis.id, selectedDiagnosis.name);
    }

    await Swal.fire({
      title: failed.length === 0 ? 'Deleted' : 'Partly deleted',
      text: failed.length === 0
        ? `${ok} record${ok === 1 ? '' : 's'} removed.`
        : `${ok} removed. Failed: ${failed.join(', ')}.`,
      icon: failed.length === 0 ? 'success' : 'warning',
      timer: failed.length === 0 ? 2200 : undefined,
      showConfirmButton: failed.length > 0,
    });
  };

  useEffect(() => { loadInsuranceProviders(); loadDiagnosesFromBackend(); }, []);

  // ── Rename the diagnosis itself ───────────────────────────────────────────
  //
  //  ASKED FOR: "there is no way to edit the name here". Every other field on
  //  these records was editable and the name was not — it was fixed when the row
  //  was created.
  //
  //  ACROSS EVERY RECORD under that name, because the list on the left is BY
  //  NAME: renaming one insurer's row would split "Abdominal Pains" into two
  //  entries, one holding three schemes and one holding a fourth. The server
  //  refuses a rename onto a name that already exists rather than merging two
  //  catalogue entries silently.
  //
  //  The claims-side picker reads this same table, so nothing has to be copied
  //  anywhere for the new name to appear there.
  const handleRename = async () => {
    if (!selectedDiagnosis) return;
    const current = selectedDiagnosis.name || '';
    const { value, isConfirmed } = await Swal.fire({
      title: 'Rename this diagnosis',
      input: 'text',
      inputValue: current,
      inputLabel: `Every insurance record filed under "${current}" is renamed with it.`,
      inputAttributes: { maxlength: 190, autocapitalize: 'off' },
      showCancelButton: true,
      confirmButtonText: 'Rename',
      //  A trailing full stop is meaningful in this catalogue — "Abdominal
      //  Pains" and "Abdominal Pains." are two priced entries — so the value is
      //  passed through as typed and only checked for being empty or unchanged.
      inputValidator: (v) => {
        const s = String(v || '').trim();
        if (!s) return 'The name cannot be empty';
        if (s === current) return 'That is already its name';
        return null;
      },
    });
    if (!isConfirmed) return;

    try {
      const { data } = await axios.post(renameDiagnosis, { from: current, to: String(value).trim() });
      if (!data.success) throw new Error(data.message || 'The rename did not go through');
      //  The left-hand list is keyed by name, so it and the selection both have
      //  to be re-read rather than patched — the entry the officer is looking at
      //  has a different identity now.
      setSelectedDiagnosis({ ...selectedDiagnosis, name: data.to });
      await loadDiagnosesFromBackend();
      await loadDiagnosisInsurancesForDisplay(selectedDiagnosis.id, data.to);
      await Swal.fire({
        title: 'Renamed', icon: 'success',
        text: `${data.changed} record${data.changed === 1 ? '' : 's'} now read "${data.to}". `
            + 'The claims screens show the new name as their catalogue refreshes.',
      });
    } catch (err) {
      await Swal.fire({
        title: 'Not renamed', icon: 'error',
        text: err.response?.data?.message || err.message,
      });
    }
  };

  // ── Will a claim actually offer this diagnosis? ───────────────────────────
  //
  //  ASKED FOR, twice: "when you add a new diagnosis to this part it doesn't show
  //   at the claim side"; then "if you add a new fresh diagnosis it doesn't show
  //   there at all, for you to use it on the claim interface."
  //
  //  This screen and the claims picker read the SAME table, so a record that does
  //  not appear was excluded by the picker's own conditions — the ICD-10, the
  //  G-DRG, the insurance scheme, the age band — and none of that was visible
  //  from here. The check runs the actual picker on the server, per scheme and
  //  per age band, and reports what it found record by record.
  const [checkingVisibility, setCheckingVisibility] = useState(false);
  const checkClaimsSide = async () => {
    if (!selectedDiagnosis) return;
    setCheckingVisibility(true);
    try {
      const { data } = await axios.get(catalogueVisibility, {
        params: { kind: 'diagnosis', name: selectedDiagnosis.name },
      });
      if (!data.success) throw new Error(data.message || 'The check did not run');
      const rows = data.rows || [];
      if (!rows.length) {
        await Swal.fire({ title: 'Nothing to check', icon: 'info',
          text: data.note || 'No insurance records are filed under this name.' });
        return;
      }
      const cell = (r) => {
        const where = r.status || 'no scheme';
        if (r.offered) {
          return `<tr><td style="padding:4px 8px">✅</td>`
               + `<td style="padding:4px 8px"><b>${where}</b></td>`
               + `<td style="padding:4px 8px">offered to ${r.offered_to.join(' and ')}</td></tr>`;
        }
        return `<tr><td style="padding:4px 8px;vertical-align:top">❌</td>`
             + `<td style="padding:4px 8px;vertical-align:top"><b>${where}</b></td>`
             + `<td style="padding:4px 8px;color:#8A5510">${r.why || 'Not offered.'}</td></tr>`;
      };
      const hidden = rows.filter((r) => !r.offered).length;
      await Swal.fire({
        title: hidden ? 'Not everything is usable' : 'Usable on claims',
        icon: hidden ? 'warning' : 'success',
        width: 720,
        html: `<div style="text-align:left;font-size:13px">`
            + `<p style="margin:0 0 8px">Checked by running the claims picker itself, `
            + `for an adult and for a child.</p>`
            + `<table style="width:100%;border-collapse:collapse">${rows.map(cell).join('')}</table>`
            + (hidden
              ? `<p style="margin:10px 0 0;color:#8A5510">A record marked ❌ can never be `
                + `picked on a claim until the reason beside it is fixed.</p>`
              : `<p style="margin:10px 0 0;color:#217A3C">Every record is offered. If it is `
                + `still not in the list on a claim already open, reload that page — the `
                + `catalogue is held for five minutes.</p>`)
            + `</div>`,
      });
    } catch (err) {
      await Swal.fire({ title: 'Could not check', icon: 'error',
        text: err.response?.data?.message || err.message });
    } finally {
      setCheckingVisibility(false);
    }
  };

  const handleDiagnosisClick = async (diagnosis) => {
    if (selectedDiagnosis?.id === diagnosis.id) { 
      setSelectedDiagnosis(null); 
      setDiagnosisInsuranceRows([]); 
      setSelectedRowIds([]);
      return; 
    }
    setSelectedDiagnosis(diagnosis);
    await loadDiagnosisInsurancesForDisplay(diagnosis.id, diagnosis.name);
  };

  const handleRowChange = (idx, field, value) =>
    setDiagnosisInsuranceRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const existingStatuses = diagnosisInsuranceRows.map(r => r.status);

  return (
    <>
      {/* One navy page header, replacing the full-width teal→lime band and its
          three competing "contained" buttons. Same treatment as Services Setup. */}
      <AdminPageHead
        icon={DiagnosisIcon}
        title="Diagnosis Setup"
        subtitle="Diagnosis tariffs and top-ups, per insurance scheme"
        actions={
          <>
            <Tooltip title="Reload diagnoses" arrow>
              <span>
                <IconButton
                  onClick={loadDiagnosesFromBackend} disabled={loadingDiagnoses} size="small"
                  aria-label="Reload diagnoses"
                  sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.14)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}
                >
                  {loadingDiagnoses ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reload insurance schemes" arrow>
              <span>
                <IconButton
                  onClick={loadInsuranceProviders} disabled={loadingInsurance} size="small"
                  aria-label="Reload insurance schemes"
                  sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.14)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}
                >
                  {loadingInsurance ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <CloudDownload sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}
              sx={{ ...brandButtonSx, py: .85, px: 2, fontSize: 13, whiteSpace: 'nowrap' }}
            >
              New Diagnosis
            </Button>
          </>
        }
      />

      <Box>
        <Box>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: ADMIN.radiusSm }} onClose={() => setError(null)}>{error}</Alert>}

          {/* Two panels on a real CSS grid. Both were `height: 580` — a fixed
              height regardless of content — which is what left the band of
              canvas under them. */}
          <Box sx={{
            display: 'grid',
            gap: { xs: 2, md: 2.5 },
            alignItems: 'start',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '340px minmax(0, 1fr)' },
          }}>

            {/* Left: diagnosis list.
                Sticky and full-height from md up, so the card fills the space
                beside the records panel instead of stopping short — and with
                1,120 diagnoses to scroll, the extra rows are the point. */}
            <Box sx={{
              minWidth: 0,
              position: { md: 'sticky' },
              top: { md: ADMIN.stickyTop.sm },
              height: { md: `calc(100vh - ${ADMIN.stickyTop.sm}px - 16px)` },
            }}>
              <Paper elevation={0} sx={{ p: 2, border: `1px solid #DDE5EF`, borderRadius: ADMIN.radius, background: '#FFFFFF', display: 'flex', flexDirection: 'column', height: { md: '100%' }, overflow: 'hidden' }}>
                <TextField fullWidth size="small" placeholder="Search diagnoses…" value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)} sx={{ ...adminFieldSx, mb: 1.5 }}
                  InputProps={{
                    startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: '#93A2B8' }} /></InputAdornment>),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton size="small" aria-label="Clear search" onClick={() => setSearchTerm('')}>
                          <CloseIcon sx={{ fontSize: 15, color: '#93A2B8' }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F1F3D' }}>Available Diagnoses</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loadingDiagnoses && <CircularProgress size={16} />}
                    <Chip size="small" label={`${diagnoses.length} total`} sx={{ fontSize: '0.68rem', background: '#E8EEF7', color: '#123A6D' }} />
                  </Box>
                </Box>
                {selectedDiagnosis && (
                  <Box sx={{ mb: 1.5, p: 1, borderRadius: '8px', background: '#E7F5EC', border: '1px solid #2E9E4F', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 15, color: '#2E9E4F', flexShrink: 0 }} />
                    <Typography noWrap sx={{ fontSize: 11.5, color: '#217A3C', fontWeight: 700, minWidth: 0 }}>{selectedDiagnosis.name}</Typography>
                    <IconButton size="small" aria-label="Clear selection" onClick={() => { setSelectedDiagnosis(null); setDiagnosisInsuranceRows([]); setSelectedRowIds([]); }} sx={{ ml: 'auto', p: 0.2, flexShrink: 0 }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                )}
                {/* `minHeight: 0` lets this flex child shrink so it scrolls
                    inside the card instead of pushing it past the viewport. */}
                <Box sx={{
                  flex: { md: 1 }, minHeight: 0,
                  maxHeight: { xs: 420, md: 'none' },
                  overflowY: 'auto', pr: 0.5,
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' },
                }}>
                  {loadingDiagnoses ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}><CircularProgress size={22} /></Box>
                  ) : diagnoses.length === 0 ? (
                    <EmptyState
                      icon={DiagnosisIcon}
                      title="No diagnoses"
                      body="Nothing loaded yet — use Reload Diagnoses in the header."
                    />
                  ) : (
                    diagnoses.filter(d => d.name?.toLowerCase().includes(searchTerm.toLowerCase())).map(diagnosis => {
                      const on = selectedDiagnosis?.id === diagnosis.id;
                      return (
                      <Box key={diagnosis.id} onClick={() => handleDiagnosisClick(diagnosis)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, mb: .375, px: 1, py: .875,
                          borderRadius: '8px', cursor: 'pointer', minWidth: 0,
                          transition: 'background-color .15s ease, border-color .15s ease',
                          border: `1px solid ${on ? '#123A6D' : 'transparent'}`,
                          background: on ? '#E8EEF7' : 'transparent',
                          // No translateX on hover. With 1,120 rows, every row
                          // shifting sideways under the pointer made the list
                          // feel unstable to scan.
                          '&:hover': { background: on ? '#E8EEF7' : 'rgba(18,58,109,0.05)' },
                        }}>
                        <DiagnosisIcon sx={{ fontSize: 15, flexShrink: 0, color: on ? '#123A6D' : '#93A2B8' }} />
                        <Typography noWrap title={diagnosis.name}
                          sx={{ fontSize: 12.5, color: on ? '#123A6D' : '#0F1F3D', fontWeight: on ? 700 : 500, minWidth: 0 }}>
                          {diagnosis.name}
                        </Typography>
                      </Box>
                      );
                    })
                  )}
                </Box>
              </Paper>
            </Box>

            {/* Right: editable records table */}
            <Box sx={{ minWidth: 0 }}>
              <Paper elevation={0} sx={{ border: `1px solid #DDE5EF`, borderRadius: ADMIN.radius, background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #DDE5EF', background: '#F4F7FB', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexShrink: 0 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F1F3D' }}>Insurance Coverage Records</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {selectedDiagnosis ? `Records for: ${selectedDiagnosis.name}` : 'Select a diagnosis to load records'}
                      </Typography>
                      {/*  ── EDIT THE NAME ITSELF ──────────────────────────────
                           Beside the name it changes, because that is what it
                           acts on. Every other field on these records was
                           editable and this one was not.

                           It renames EVERY record under this name — the list on
                           the left is by name, so renaming one insurer's row
                           would split the entry in two. */}
                      {selectedDiagnosis && (
                        <Tooltip title="Rename this diagnosis — every insurance record under it">
                          <IconButton
                            size="small"
                            onClick={handleRename}
                            aria-label={`Rename ${selectedDiagnosis.name}`}
                            sx={{ p: 0.25, color: ADMIN.brand || '#1976d2' }}>
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/*  ── WILL A CLAIM ACTUALLY OFFER THIS? ────────────────
                           ASKED FOR: "when you add a new diagnosis it doesn't
                           show at the claim side — I don't know how true it is."
                           This screen writes to the same table the claims picker
                           reads, so when a record does not appear it is because
                           the picker excluded it, and nothing here said which of
                           its conditions the row failed. This runs the real
                           picker and reports, per insurance record, yes or no
                           and why. */}
                      {selectedDiagnosis && (
                        <Tooltip title="Check whether the claims side can offer this diagnosis">
                          <IconButton
                            size="small"
                            onClick={checkClaimsSide}
                            disabled={checkingVisibility}
                            aria-label={`Check the claims side for ${selectedDiagnosis.name}`}
                            sx={{ p: 0.25, color: '#217A3C' }}>
                            <VisibilityIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {selectedRowIds.length > 0 && (
                      <Chip 
                        size="small" 
                        label={`${selectedRowIds.length} selected`}
                        sx={{ background: '#e3f2fd', color: '#1976d2', fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    )}
                    {selectedDiagnosis && !loadingDiagnosisInsurances && (
                      <Chip icon={<InsuranceIcon sx={{ fontSize: '14px !important' }} />}
                        label={`${diagnosisInsuranceRows.length} record${diagnosisInsuranceRows.length !== 1 ? 's' : ''}`} size="small"
                        sx={{ background: diagnosisInsuranceRows.length > 0 ? '#E7F5EC' : '#FDECEC', color: diagnosisInsuranceRows.length > 0 ? '#217A3C' : '#B23A3A', fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                  </Box>
                </Box>

                {/* Bulk edit bar. Type once, apply to every ticked scheme.
                    Replaces a strip whose only bulk action was "select exactly
                    ONE row, then copy it to other schemes" — which could not
                    help when every row still needed its first value. */}
                {selectedDiagnosis && diagnosisInsuranceRows.length > 0 && (
                  <BulkEditBar
                    fields={BULK_FIELDS}
                    values={bulkValues}
                    onChange={(k, v) => setBulkValues(prev => ({ ...prev, [k]: v }))}
                    selectedCount={selectedRowIds.length}
                    totalCount={diagnosisInsuranceRows.length}
                    onApply={handleBarApply}
                    onDelete={handleBarDelete}
                    applying={bulkApplying}
                    deleting={bulkDeleting}
                    itemLabel="scheme"
                  />
                )}

                {/* The old row-as-template flow, kept as a secondary action.
                    It does a different job — it ADDS this diagnosis to schemes
                    that do not have it yet, where the bar EDITS rows that
                    already exist. Both are useful; only one was here before. */}
                {selectedDiagnosis && diagnosisInsuranceRows.length > 0 && (
                  <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1, borderBottom: '1px solid #DDE5EF', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flexShrink: 0 }}>
                    <Button
                      size="small" variant="text" startIcon={<CopyIcon sx={{ fontSize: 15 }} />}
                      onClick={handleBulkApply}
                      disabled={selectedRowIds.length !== 1}
                      sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, color: '#123A6D', py: .3 }}
                    >
                      Copy one row to other schemes
                    </Button>
                    <Typography sx={{ fontSize: 11, color: '#93A2B8' }}>
                      {selectedRowIds.length === 1
                        ? 'Adds this diagnosis to schemes that do not have it yet'
                        : 'Tick exactly one row to use it as the template'}
                    </Typography>
                  </Box>
                )}

                {/* Three placeholder states that each filled a 580px panel with
                    a faded pastel icon, so the card stayed tall even with
                    nothing in it. Normal empty states now. */}
                {!selectedDiagnosis ? (
                  <EmptyState
                    icon={InsuranceIcon}
                    title="Pick a diagnosis"
                    body="Choose one from the list on the left to see and edit the tariff each scheme is charged."
                  />
                ) : loadingDiagnosisInsurances ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, gap: 1.5 }}>
                    <CircularProgress size={20} sx={{ color: '#123A6D' }} />
                    <Typography sx={{ fontSize: 13, color: '#5A6B87' }}>Loading records…</Typography>
                  </Box>
                ) : diagnosisInsuranceRows.length === 0 ? (
                  <EmptyState
                    icon={InsuranceIcon}
                    title="No schemes on this diagnosis"
                    body={`Nothing is configured for ${selectedDiagnosis.name} yet.`}
                  />
                ) : (
                  <TableContainer sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { width: '6px', height: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' } }}>
                    {/*  minWidth was 1200, which put Actions past the right edge
                         of the panel on any normal laptop — so saving a row meant
                         scrolling sideways to find the button. 900 is a real
                         floor: below it the number inputs stop being usable and
                         scrolling is the honest answer, which is what a phone
                         gets. Above it the table simply fits. */}
                    <Table size="small" sx={{ minWidth: 900 }}>
                      <TableHead>
                        <TableRow>
                          {/* Was a solid green band with white text — heavier
                              than the data under it, and the third green on the
                              screen. Quiet uppercase labels on the canvas tint,
                              matching every other admin table. */}
                          <TableCell sx={{ background: '#F4F7FB', borderBottom: '1px solid #DDE5EF', width: 48, textAlign: 'center', py: 1.25, px: 0.5 }}>
                            <Checkbox
                              size="small"
                              checked={selectedRowIds.length === diagnosisInsuranceRows.length && diagnosisInsuranceRows.length > 0}
                              indeterminate={selectedRowIds.length > 0 && selectedRowIds.length < diagnosisInsuranceRows.length}
                              onChange={handleSelectAllRows}
                              sx={{ color: '#93A2B8', '&.Mui-checked': { color: '#2E9E4F' }, '&.MuiCheckbox-indeterminate': { color: '#2E9E4F' }, p: 0.5 }}
                            />
                          </TableCell>
                          {/* 'Core' renamed to 'GDRG Code' — that column holds
                              the GDRG the claim is actually billed on, and
                              calling it Core hid that. 'Code' at the end is the
                              specialty code, so it now says so. */}
                          {['#', 'Insurance', 'Tariff (GH¢)', 'Top-up', 'Age', 'ICD-10', 'GDRG Code', 'Specialty', 'Actions'].map(label => (
                            <TableCell key={label} sx={{ background: '#F4F7FB', color: '#93A2B8', fontWeight: 800, fontSize: '0.65rem', letterSpacing: '1px', textTransform: 'uppercase', borderBottom: '1px solid #DDE5EF', py: 1.25, whiteSpace: 'nowrap', ...(label === '#' && { width: 36, textAlign: 'center' }), ...(label === 'Actions' && { width: 96, textAlign: 'center' }) }}>
                              {label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {diagnosisInsuranceRows.map((row, idx) => (
                          <EditableRow 
                            key={row.id || idx} 
                            row={row} 
                            idx={idx}
                            selected={selectedRowIds.includes(row.id)}
                            onSelectToggle={handleRowSelectionToggle}
                            onChange={(field, value) => handleRowChange(idx, field, value)}
                            onUpdate={handleUpdateRow} 
                            onDelete={handleDeleteRow}
                            updating={updatingRowId === row.id} 
                            deleting={deletingRowId === row.id} 
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Box>
          </Box>
        </Box>
      </Box>

      <NewDiagnosisDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        insuranceProviders={insuranceProviders}
        loadingInsurance={loadingInsurance}
        onSaved={loadDiagnosesFromBackend}
      />

      <BulkApplyDialog
        open={bulkApplyDialogOpen}
        onClose={() => {
          setBulkApplyDialogOpen(false);
          setSourceRowForBulkApply(null);
        }}
        sourceRow={sourceRowForBulkApply}
        insuranceProviders={insuranceProviders}
        loadingInsurance={loadingInsurance}
        onSaved={() => {
          if (selectedDiagnosis) {
            loadDiagnosisInsurancesForDisplay(selectedDiagnosis.id, selectedDiagnosis.name);
          }
        }}
        existingStatuses={existingStatuses}
      />
    </>
  );
};

export default Diagnosis;





